// src/pages/Home.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Container,
  Card,
  CardContent,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
} from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import YouTubeIcon from "@mui/icons-material/YouTube";
import ChatIcon from "@mui/icons-material/Chat";
import { Helmet } from "react-helmet";
import {
  doc,
  updateDoc,
  serverTimestamp,
  collection,
  query,
  where,
  getDocs,
  limit,
} from "firebase/firestore";
import { db } from "../firebase";

function normalizePhone(input) {
  // deja solo dígitos
  const digits = (input || "").replace(/\D/g, "");
  // si viene con 1 al inicio y tiene 11 (RD/US), lo dejamos igual
  // si tiene 10, lo dejamos igual
  // si tiene menos, igual lo devolvemos para validación
  return digits;
}

function sanitizeUsername(input) {
  const raw = (input || "").trim();
  // permite letras, números, guion y guion bajo
  return raw.replace(/[^\w-]/g, "").slice(0, 20);
}

function Home() {
  const navigate = useNavigate();

  const username = localStorage.getItem("username");
  const isGuest = localStorage.getItem("isGuest") === "1";

  // Ajusta aquí si tu campo no se llama "phone"
  const PHONE_FIELDS = useMemo(() => ["phone", "telefono", "tel"], []);

  const [openEnter, setOpenEnter] = useState(false);
  const [step, setStep] = useState(1); // 1 = pedir phone, 2 = pedir username invitado
  const [phoneInput, setPhoneInput] = useState("");
  const [guestUsernameInput, setGuestUsernameInput] = useState("");
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState("");

  const updateLastOnline = async () => {
    if (!username || isGuest) return;
    try {
      const ref = doc(db, "users", username);
      await updateDoc(ref, { lastOnline: serverTimestamp() });
    } catch (err) {
      console.error("Error al actualizar lastOnline:", err);
    }
  };

  useEffect(() => {
    updateLastOnline();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goToGranRifa = () => navigate("/tombola");

  const openYouTube = () =>
    window.open("https://www.youtube.com/shorts/aijwhyShpFg", "_blank");

  const openWhatsApp = () =>
    window.open(
      "https://wa.me/18096245631?text=Hola, mi cuenta ha sido bloqueada y necesito asistencia.",
      "_blank"
    );

  const resetEnterModal = () => {
    setStep(1);
    setPhoneInput("");
    setGuestUsernameInput("");
    setChecking(false);
    setError("");
  };

  const openEnterModal = () => {
    resetEnterModal();
    setOpenEnter(true);
  };

  const closeEnterModal = () => {
    setOpenEnter(false);
    resetEnterModal();
  };

  const findUserByPhone = async (normalizedPhone) => {
    // Probamos buscar por cualquiera de estos campos: phone / telefono / tel
    // Retorna { found: boolean, username: string|null }
    for (const field of PHONE_FIELDS) {
      const q = query(
        collection(db, "users"),
        where(field, "==", normalizedPhone),
        limit(1)
      );
      const snap = await getDocs(q);
      if (!snap.empty) {
        const d = snap.docs[0];
        const data = d.data() || {};
        const resolvedUsername = data.username || data.user || d.id; // fallback al id
        return { found: true, username: resolvedUsername };
      }
    }
    return { found: false, username: null };
  };

  const handlePhoneContinue = async () => {
    setError("");
    const normalized = normalizePhone(phoneInput);

    // Validación simple: 10 u 11 dígitos (ajusta si necesitas)
    if (normalized.length < 10) {
      setError("Por favor, escribe un número de teléfono válido.");
      return;
    }

    setChecking(true);
    try {
      const result = await findUserByPhone(normalized);

      if (result.found && result.username) {
        // Usuario registrado
        localStorage.setItem("username", result.username);
        localStorage.setItem("phone", normalized);
        localStorage.setItem("isGuest", "0");

        closeEnterModal();
        navigate("/chat"); // ajusta la ruta si tu chat es otra
        return;
      }

      // No encontrado -> pedir username invitado
      localStorage.setItem("phone", normalized);
      setStep(2);
    } catch (e) {
      console.error(e);
      setError("No se pudo verificar el teléfono. Intenta de nuevo.");
    } finally {
      setChecking(false);
    }
  };

  const handleGuestEnter = () => {
    setError("");
    const normalized = normalizePhone(localStorage.getItem("phone") || phoneInput);
    const cleaned = sanitizeUsername(guestUsernameInput);

    if (!normalized || normalized.length < 10) {
      setError("Teléfono inválido. Vuelve atrás y colócalo de nuevo.");
      setStep(1);
      return;
    }

    if (!cleaned || cleaned.length < 3) {
      setError("Escribe un nombre de usuario (mínimo 3 caracteres).");
      return;
    }

    // Invitado (no se guarda en BD)
    localStorage.setItem("username", cleaned);
    localStorage.setItem("phone", normalized);
    localStorage.setItem("isGuest", "1");

    closeEnterModal();
    navigate("/chat"); // ajusta la ruta si tu chat es otra
  };

  return (
    <>
      <Helmet>
        <title>Chat SRD - Comunidad de Loterías</title>
      </Helmet>

      <Container maxWidth="sm" sx={{ mt: 6, mb: 8 }}>
        <Box
          sx={{
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          {/* LOGO */}
          <Box>
            <img
              src="https://chatsrd.com/logo.png"
              alt="Logo de Chat SRD"
              style={{
                width: "70%",
                maxWidth: "240px",
                borderRadius: "16px",
                boxShadow: "0 6px 15px rgba(0,0,0,0.2)",
              }}
            />
          </Box>

          {/* TARJETA */}
          <Card
            sx={{
              borderRadius: "16px",
              background: "#f4f7fb",
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            }}
          >
            <CardContent>
              <Typography
                variant="h5"
                fontWeight="bold"
                color="primary"
                gutterBottom
              >
                ¡Bienvenido a Chat SRD!
              </Typography>

              <Typography variant="body1" color="text.secondary">
                Únete a la comunidad, comparte números, aprende de otros jugadores y
                disfruta de las mejores predicciones diarias.
              </Typography>
            </CardContent>
          </Card>

          {/* BOTONES */}
          <Stack spacing={2} sx={{ width: "100%" }}>
            {/* NUEVO BOTÓN: ENTRAR AL CHAT */}
            <Button
              variant="contained"
              startIcon={<ChatIcon />}
              onClick={openEnterModal}
              sx={{
                width: "100%",
                backgroundColor: "#4caf50",
                fontWeight: "bold",
                borderRadius: "10px",
                "&:hover": { backgroundColor: "#3a8d3d" },
              }}
            >
              Entrar al chat
            </Button>

            

            {/* BOTÓN TÓMBOLA 
            <Button
              variant="contained"
              onClick={goToGranRifa}
              sx={{
                width: "100%",
                backgroundColor: "#ff9800",
                fontWeight: "bold",
                borderRadius: "10px",
                "&:hover": { backgroundColor: "#e68900" },
              }}
            >
              🎟 Entra a la Tombola
            </Button>*/}

            
<Button
  variant="contained"
  onClick={() => navigate("/predicciones")}
  sx={{
    width: "100%",
    backgroundColor: "#1976d2",
    fontWeight: "bold",
    borderRadius: "10px",
    "&:hover": { backgroundColor: "#115293" },
  }}
>
  🔮 Predicciones
</Button>


            <Button
              variant="contained"
              startIcon={<YouTubeIcon />}
              onClick={openYouTube}
              sx={{
                backgroundColor: "#f44336",
                fontWeight: "bold",
                "&:hover": { backgroundColor: "#d32f2f" },
              }}
            >
              Ver Tutorial en YouTube
            </Button>

            <Button
              variant="outlined"
              startIcon={<WhatsAppIcon />}
              onClick={openWhatsApp}
              sx={{
                borderColor: "#25d366",
                color: "#25d366",
                fontWeight: "bold",
                "&:hover": { borderColor: "#1fae53", color: "#1fae53" },
              }}
            >
              Soporte por WhatsApp
            </Button>
          </Stack>
        </Box>
      </Container>

      {/* MODAL: ENTRAR AL CHAT */}
      <Dialog open={openEnter} onClose={closeEnterModal} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 800 }}>
          {step === 1 ? "Entrar al chat" : "Entrar como invitado"}
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}

          {step === 1 ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Escribe tu número. Si está registrado, entrarás automáticamente con tu
                usuario. Si no, podrás entrar como invitado.
              </Typography>

              <TextField
                autoFocus
                fullWidth
                label="Número de teléfono"
                placeholder="Ej: 8096245631"
                value={phoneInput}
                onChange={(e) => setPhoneInput(e.target.value)}
                inputProps={{ inputMode: "numeric" }}
              />
            </>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                No encontramos ese teléfono registrado. Elige un nombre para entrar como
                invitado.
              </Typography>

              <TextField
                autoFocus
                fullWidth
                label="Nombre de usuario"
                placeholder="Ej: MarcosValerio"
                value={guestUsernameInput}
                onChange={(e) => setGuestUsernameInput(e.target.value)}
              />

              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                Solo letras/números/guiones. No se guardará en la base de datos.
              </Typography>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          {step === 2 ? (
            <Button
              variant="text"
              onClick={() => {
                setError("");
                setStep(1);
              }}
            >
              Atrás
            </Button>
          ) : (
            <Button variant="text" onClick={closeEnterModal}>
              Cancelar
            </Button>
          )}

          {step === 1 ? (
            <Button
              variant="contained"
              onClick={handlePhoneContinue}
              disabled={checking}
              sx={{ backgroundColor: "#4caf50", "&:hover": { backgroundColor: "#3a8d3d" } }}
            >
              {checking ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={18} color="inherit" />
                  Verificando...
                </Box>
              ) : (
                "Continuar"
              )}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleGuestEnter}
              sx={{ backgroundColor: "#4caf50", "&:hover": { backgroundColor: "#3a8d3d" } }}
            >
              Entrar
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}

export default Home;
